import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Formation } from '@shared/models/formation.model';
import { PaginatedData } from '@shared/models/paginated-data';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { UserService } from '@shared/services/user.service';
import { ScrollbarsAutoHideBehavior } from 'overlayscrollbars';
import { Subject, switchMap } from 'rxjs';
import { Profile } from 'src/app/shared/models/profile.model';
import { NavigationService } from '../../../core/services/navigation.service';
import { NURSE, SURGEON } from '../../const/globals.const';
import { Specialty } from '../../models/specialty.model';
import { ErrorService } from '../../services/error.service';
import { ProfileService } from '../../services/profile.service';

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  standalone: false,
})
export class SearchBarComponent implements OnChanges, OnInit, OnDestroy {
  private instanceId: string;

  @Input() public fullWidth: boolean = false;

  @Input() public emitProfileObject: boolean = false;

  @Input() public isParent: boolean = false;

  @Input() public type: string | undefined = NURSE;

  @Input() public emitProfile: boolean = false;

  @Input() public justTitular: boolean = false;

  @Input() public noUrgences: boolean = false;

  @Input() public isAllProfiles: boolean = true;

  @Input() public onlyInterns: boolean = false;

  @Input() public isNoUserAssociate: boolean = false;

  @Input() public multiType: any = [];

  @Input() public fichePoste: boolean = false;

  @Input() public individual: boolean = false;

  @Input() public disabled: boolean = false;

  @Input() public placeholder: string = 'Rechercher une personne ...';

  @Input() public label: string = '';

  @Input() public hintMessage: string = '';

  @Input() public hospitalId: string | undefined;

  //iconType : 'image' / 'mat-icon'.
  @Input() public iconType: string = 'mat-icon';

  //Icon : button icon : svg / mat-icon.
  @Input() public icon: string = 'search';

  @Input() public selectedProfile: Profile | undefined;

  @Input() public specialty: any;

  @Input() public positions: string[] | undefined;

  @Input() public seniority: string[] | undefined;

  @Input() public residency: string[] | undefined;

  @Input() public specialties: string[] | undefined;

  @Input() public includeDesactivated: boolean | undefined;

  @Input() public showClearButton: boolean = false;

  @Output() public onRouterChange = new EventEmitter<string>();

  @Output() public onProfileSelected = new EventEmitter<any>();

  @Output() public onMultipleProfile = new EventEmitter<any>();

  @Output() public isLoadingChange = new EventEmitter<boolean>();

  private readonly searchSubject$ = new Subject<string>();

  private readonly pageSize: number = 10;

  private currentPage: number = 1;

  private rawProfilesList: any[] = [];

  public hasMoreResults: boolean = false;

  public totalDocs: number = 0;

  private formationPopulation: string | undefined;

  private formationsGroups: string | undefined;

  public lastSearchValue: string = '';

  public isShowSearchResults: boolean = false;

  @ViewChild('search', {}) private readonly searchElement: ElementRef | undefined;

  public isLoading: boolean = false;

  public profilesList: any[] = [];

  public osComponentOptions = {
    className: 'os-theme-dark custom-sidebar',
    nativeScrollbarsOverlaid: {
      showNativeScrollbars: false,
    },
    overflowBehavior: {
      x: 'hidden',
    },
    paddingAbsolute: true,
    scrollbars: {
      autoHide: 'never' as ScrollbarsAutoHideBehavior,
    },
  };

  constructor(
    private profileService: ProfileService,
    private errorService: ErrorService,
    private navigationService: NavigationService,
    private userService: UserService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnInit() {
    this.initializeSearchDebounce();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.disabled) {
      await this.prepareFormationPopulation();
      await this.prepareFormationsGroups();
      if (changes['specialties'] && !changes['specialties'].firstChange) {
        this.loadInitialProfiles();
      }
    }
    if (this.disabled && this.emitProfileObject) {
      this.selectedProfile = undefined;
      this.onProfileSelected.emit(this.selectedProfile);
    }
  }

  onEnter(searchInput: string): void {
    this.lastSearchValue = searchInput;
    this.searchSubject$.next(searchInput);
  }

  private initializeSearchDebounce(): void {
    this.subscriptionManager
      .createRequest(this.searchSubject$, 'searchSubject', this.instanceId, false, 500, true)
      .pipe(
        switchMap((searchTerm: string) => {
          this.isLoading = true;
          this.isLoadingChange.emit(this.isLoading);
          this.currentPage = 1;
          return this.profileService.searchProfiles({
            page: this.currentPage,
            limit: this.pageSize,
            search: searchTerm,
            population: this.formationPopulation,
            seniorities: this.seniority?.join(', '),
            formationsGroups: this.formationsGroups,
            specialties: this.specialties?.join(', '),
            includeDesactivated: this.includeDesactivated,
          });
        })
      )
      .subscribe({
        next: (response: any) => this.handleSearchResponse(response),
        error: (error: any) => this.handleSearchError(error),
      });
  }

  private async prepareFormationPopulation(): Promise<void> {
    let formations: string[] = [];

    if (this.type !== SURGEON) {
      const formationObjects = await this.userService.getAccessOptionsPositionsByPositions(this.positions, true);
      formations = formationObjects
        .map((formation: Formation) => formation._id)
        .filter((id): id is string => id !== undefined);
    } else {
      const surgeonsFormations = await this.userService.getFormationsByGroupName(undefined, 'SURGEONS', true);
      formations = surgeonsFormations.map((formation: any) => formation._id);
    }

    this.formationPopulation = formations.length > 0 ? JSON.stringify(formations) : undefined;
  }

  private async prepareFormationsGroups(): Promise<void> {
    const preparedFormationsGroups = await this.userService.getFormationsGroupsOfAccessOption(this.positions || []);
    const formationsGroupsIds = preparedFormationsGroups.map((fg) => fg._id);
    this.formationsGroups = formationsGroupsIds.length ? JSON.stringify(formationsGroupsIds) : undefined;
  }

  private loadInitialProfiles(): void {
    this.isLoading = true;
    this.isLoadingChange.emit(this.isLoading);

    this.subscriptionManager
      .createRequest(
        this.profileService.searchProfiles({
          page: 1,
          limit: this.pageSize,
          search: '',
          population: this.formationPopulation,
          seniorities: this.seniority?.join(', '),
          formationsGroups: this.formationsGroups,
          specialties: this.specialties?.join(', '),
          includeDesactivated: this.includeDesactivated,
        }),
        'searchProfiles',
        this.instanceId
      )
      .subscribe({
        next: (response: any) => {
          this.currentPage = 1;
          this.handleSearchResponse(response);
        },
        error: (error: any) => this.handleSearchError(error),
      });
  }

  loadMoreResults(): void {
    if (this.isLoading || !this.hasMoreResults) {
      return;
    }

    this.isLoading = true;
    this.isLoadingChange.emit(this.isLoading);
    this.currentPage++;

    this.subscriptionManager
      .createRequest(
        this.profileService.searchProfiles({
          page: this.currentPage,
          limit: this.pageSize,
          search: this.lastSearchValue,
          population: this.formationPopulation,
          seniorities: this.seniority?.join(', '),
          formationsGroups: this.formationsGroups,
          specialties: this.specialties?.join(', '),
          includeDesactivated: this.includeDesactivated,
        }),
        'searchProfiles',
        this.instanceId
      )
      .subscribe({
        next: (response: PaginatedData<Profile>) => {
          this.isLoading = false;
          this.isLoadingChange.emit(this.isLoading);
          this.profilesList = [...this.profilesList, ...(response.docs || [])];
          this.rawProfilesList = [...this.rawProfilesList, ...(response.docs || [])];
          this.hasMoreResults = response.hasNextPage || false;
        },
        error: (error: any) => this.handleSearchError(error),
      });
  }

  private handleSearchResponse(response: any): void {
    this.isLoading = false;
    this.isLoadingChange.emit(this.isLoading);
    this.profilesList = response.docs || [];
    this.rawProfilesList = response.docs || [];
    this.totalDocs = response.totalDocs || 0;
    this.hasMoreResults = response.hasNextPage || false;

    if (this.specialty) {
      this.filterBySpecialty();
    }

    this.onMultipleProfile.emit(this.profilesList);
  }

  private handleSearchError(error: any): void {
    this.isLoading = false;
    this.isLoadingChange.emit(this.isLoading);
    this.errorService.handleError(error);
  }

  toggleSearchResults(event: any): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isShowSearchResults) {
      this.isShowSearchResults = !this.isShowSearchResults;
      this.searchElement?.nativeElement.blur();
      this.hideInputText();
    } else {
      this.isShowSearchResults = !this.isShowSearchResults;
      this.searchElement?.nativeElement.focus();
      this.showInputText();
      if (this.profilesList.length === 0) {
        this.loadInitialProfiles();
      }
    }
  }

  showSearchResults(event: any): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.searchElement) {
      this.searchElement.nativeElement.focus();
      this.isShowSearchResults = true;
      this.searchElement.nativeElement.value = this.lastSearchValue;

      if (this.profilesList.length === 0) {
        this.loadInitialProfiles();
      }
    }
  }

  hideInputText(): void {
    // we hide input text if we are not searching and we have a selected profile
    if (this.selectedProfile && this.searchElement) {
      this.searchElement.nativeElement.value = '';
    }
  }

  showInputText(): void {
    // We show input text if we are searching
    if (this.searchElement) {
      this.searchElement.nativeElement.value = this.lastSearchValue;
    }
  }

  profileClick(profile: Profile, _event: any): void {
    this.selectedProfile = profile;
    const id = profile._id;
    if (this.emitProfileObject) {
      this.onProfileSelected.emit(profile);
      this.isShowSearchResults = false;
      return;
    } else if (this.emitProfile) {
      this.onProfileSelected.emit(id);
      this.isShowSearchResults = false;
      return;
    }

    let prefix;
    if (!this.fichePoste) {
      if (this.type === NURSE) {
        prefix = 'parcours-ibode/competence/competence-individuelle/';
      } else if (this.type === SURGEON) {
        prefix = 'statop/bloc/par-praticien/';
      } else {
        prefix = 'configuration/advanced/fiche-poste/';
      }
    } else if (this.individual) {
      prefix = 'configuration/advanced/fiche-poste/individual/';
    } else {
      prefix = 'configuration/advanced/fiche-poste/';
    }

    this.navigationService
      .navigateByUrl(prefix + id)
      .then(() => {
        if (!this.isParent) {
          this.onRouterChange.emit('refresh');
        }
      })
      .catch();
  }

  filterBySpecialty(): void {
    this.profilesList = (this.rawProfilesList ?? []).filter((profile: Profile) =>
      (profile?.specialties ?? []).map((specialty: Specialty) => specialty._id).includes(this.specialty)
    );
  }

  clearInput(event: any): void {
    event.stopPropagation();
    if (this.searchElement) {
      this.selectedProfile = undefined;
      this.searchElement.nativeElement.value = '';
      this.lastSearchValue = '';
      this.onEnter('');
      this.searchElement.nativeElement.focus();
      if (this.emitProfileObject) {
        this.onProfileSelected.emit({});
      } else if (this.emitProfile) {
        this.onProfileSelected.emit('');
      }
    }
  }

  onScroll(index: number): void {
    if (index > this.profilesList.length - 10 && this.hasMoreResults) {
      this.loadMoreResults();
    }
  }

  @HostListener('window:click', ['$event'])
  click(_event: any) {
    this.isShowSearchResults = false;
    this.hideInputText();
  }

  ngOnDestroy() {
    this.searchSubject$.complete();
  }
}
